import { useParams, useNavigate } from "react-router-dom";
import { Clock, Package, Heart, Info, UserRound, HandHeart, ArrowRight } from "lucide-react";
import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import Topography from "@/components/Topography";
import { CopyLinkButton } from "@/components/common/CopyLinkButton";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-brand-navy/10 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-brand-orange/12 flex items-center justify-center">
          <Icon className="w-5 h-5 text-brand-orange" />
        </div>
        <h2 className="display-title text-xl text-brand-navy">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function ActivityOverview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { activity, status } = useActivity(slug);
  useSeo(
    activity ? activity.name : "Activity",
    activity ? activity.shortDescription : "Project in a Box activity overview."
  );

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity) return <ErrorState onRetry={() => window.location.reload()} />;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy text-white">
        <Topography color="#F5F0E1" opacity={0.08} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
              {activity.directoryCard?.categoryTag || activity.categories.join(" + ")}
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-white/80">
              <Clock className="w-4 h-4" /> {activity.duration}
            </span>
          </div>
          <h1 className="display-title text-3xl sm:text-5xl">{activity.name}</h1>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 space-y-5">
        {/* Role choice cards — primary purpose of this page, kept above the fold */}
        <div className="pt-1">
          <h2 className="display-title text-2xl text-brand-navy">How would you like to start?</h2>
          <p className="mt-1 text-brand-navy/70">Choose your role for this activity.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div
              data-testid="role-card-organizer"
              className="group flex flex-col rounded-2xl bg-brand-orange text-white p-6 pib-shadow"
            >
              <button
                type="button"
                data-testid="cta-organizer"
                onClick={() => navigate(`/activities/${slug}/organizer`)}
                className="text-left flex-1"
              >
                <UserRound className="w-8 h-8 mb-3" />
                <h3 className="display-title text-2xl">I'm an Organizer</h3>
                <p className="mt-1 text-white/90">I'm setting up or running an event.</p>
                <span className="mt-3 inline-flex items-center gap-1.5 font-semibold">
                  Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              {!activity.comingSoon && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <CopyLinkButton
                    path={`/api/share/${slug}/organizer`}
                    testid="copy-link-organizer"
                    variant="onDark"
                    label="Copy link"
                  />
                </div>
              )}
            </div>

            <div
              data-testid="role-card-volunteer"
              className="group flex flex-col rounded-2xl bg-white border-2 border-brand-navy/15 p-6 pib-shadow"
            >
              <button
                type="button"
                data-testid="cta-volunteer"
                onClick={() => navigate(`/activities/${slug}/volunteer`)}
                className="text-left flex-1"
              >
                <HandHeart className="w-8 h-8 mb-3 text-brand-orange" />
                <h3 className="display-title text-2xl text-brand-navy">I'm a Volunteer</h3>
                <p className="mt-1 text-brand-navy/70">I'm here to do the activity.</p>
                <span className="mt-3 inline-flex items-center gap-1.5 font-semibold text-brand-orange">
                  Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              {!activity.comingSoon && (
                <div className="mt-4 pt-4 border-t border-brand-navy/10">
                  <CopyLinkButton
                    path={`/api/share/${slug}/volunteer`}
                    testid="copy-link-volunteer"
                    variant="ghost"
                    label="Copy link"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <Section icon={Info} title="About this activity">
          <p className="text-brand-navy/80 leading-relaxed">{activity.about}</p>
        </Section>

        <Section icon={Heart} title="Who it helps">
          <p className="text-brand-navy/80 leading-relaxed">{activity.impact}</p>
        </Section>

        <Section icon={Package} title="What's in the kit">
          <p className="text-sm text-brand-navy/60 mb-3">1 kit has the following items:</p>
          <ul className="grid sm:grid-cols-2 gap-2">
            {activity.materialsPerKit.map((m, i) => (
              <li key={i} className="flex items-start justify-between gap-3 rounded-xl bg-brand-cream px-3.5 py-2.5">
                <span className="text-[15px] text-brand-navy">{m.name}</span>
                <span className="flex-shrink-0 text-sm font-semibold text-brand-orange whitespace-nowrap">{m.qty}</span>
              </li>
            ))}
          </ul>
          {activity.materialsNote && (
            <p className="mt-3 text-sm text-brand-navy/60 italic" data-testid="materials-note">{activity.materialsNote}</p>
          )}
        </Section>
      </div>
    </div>
  );
}
